/**
 * Shared constants for component drop/placement validation.
 * Used by both MCP component-tools.ts and drag-drop-helper.ts.
 *
 * IMPORTANT: All component names in HSTACK_ALLOWED_SET are lowercase
 * because callers normalise with `.toLowerCase()` before checking.
 */

/** Components allowed inside HorizontalStack (all lowercase). */
export const HSTACK_ALLOWED_SET: ReadonlySet<string> = new Set([
  "label",
  "button",
  "textinput",
  "image",
  "icon",
  "tooltip",
  "thumbnail",
  "barcoderenderer",
  "badge",
  "locationsearch",
  "numberinput",
  "email",
  "textarea",
  "currency",
  "password",
  "percent",
  "url",
  "autocomplete",
  "selectbox",
  "multiselect",
  "checkbox",
  "radiogroup",
  "switch",
  "contentmenu",
  "date",
  "daterange",
  "datetimerange",
  "datetime",
  "monthpicker",
  "yearpicker",
  "countup",
  "countdown",
  "rate",
  "language selector",
  "filedownload",
  "qrrenderer",
  "horizontalstack",
  "verticalstack",
  "dynamicinput",
  "colorpicker",
  "progressbar"
]);

/**
 * Components that render as a fixed floating overlay (a FAB) and must therefore
 * NEVER be wrapped in a default Row/Col/Panel — they go straight to the screen root.
 * Wrapping them produces a phantom empty layout row and can trap their `position:fixed`
 * under a transformed layout ancestor. Enforced centrally inside the wrap helpers in
 * drag-drop-helper.ts, so every add path (canvas drag-drop, item tree, MCP) is covered.
 */
export const FIXED_OVERLAY_COMPONENTS: ReadonlySet<string> = new Set(["FloatButton", "AgentChat"]);

/** Components that skip automatic Row > Col wrapping when added to Body. */
export const SKIP_ROW_WRAP_SET: ReadonlySet<string> = new Set([
  "Row",
  "Col",
  "FloatButton",
  // The agent chat renders as a floating FAB (like FloatButton), so it must go straight
  // to the root rather than being nested in a layout Row/Col. ("AgentChat" is the
  // component's display name; its technical tagName/renderer is still TwinChatLive.)
  "AgentChat",
  "Header",
  "Footer",
  "Panel",
  "Table",
  "StaticTable",
  "ReportTable",
  "Form",
  "VerticalStack",
  "HorizontalStack"
]);

/** Table-like parents that only accept column components as direct children. */
export const TABLE_TARGETS: ReadonlySet<string> = new Set([
  "Table",
  "ReportTable",
  "TableRow",
  "ReportTableRow",
  "TableHeader",
  "ReportTableHeader"
]);

/** Valid column component types. */
export const COLUMN_COMPONENTS: ReadonlySet<string> = new Set([
  "TableColumn",
  "ReportTableColumn",
  "EditableTableColumn",
  "TableDefaultColumn",
  "TableTemplateColumn",
  "StaticTableColumn"
]);

/**
 * Components marked as ComponentType.simple in Components.json that still
 * accept child components — exception to the rule-7 "simple containers
 * cannot have children" check in component-drop-validation.ts.
 *
 * Includes:
 * - Table column types (TableColumn / ReportTableColumn / TableTemplateColumn /
 *   StaticTableColumn) which host arbitrary cell content despite the simple
 *   componentType marker.
 * - The StaticTable family (StaticTable / StaticTableHeader / StaticTableRow)
 *   which is also marked simple in Components.json yet legitimately hosts
 *   header / body row / column children. Without these entries, MCP-driven
 *   `<StaticTable><StaticTableHeader>...` markup fails silently at
 *   validateDrop (rule 7 rejects the nested adds), every StaticTable cell
 *   becomes an orphan kuikaId, and the canvas shows only the empty wrapper
 *   (KOA-16241 — "5 örnek satır" regression where the runtime applied 3 of
 *   29 add calls and 24 alias refs landed unmatched).
 */
export const TABLE_COLUMN_CONTAINERS: ReadonlySet<string> = new Set([
  "TableColumn",
  "ReportTableColumn",
  "TableTemplateColumn",
  "StaticTableColumn",
  "StaticTable",
  "StaticTableHeader",
  "StaticTableRow"
]);

/**
 * StaticTable-family STRUCTURAL parents and the only child component(s) each may host directly.
 * StaticTable hosts the header/body bands; StaticTableRow / StaticTableHeader host column cells.
 *
 * Enforced by validateComponentDrop so a bare element (Icon/Label/…) can never become a direct
 * child of a StaticTable / StaticTableRow / StaticTableHeader without a StaticTableColumn wrapper —
 * that orphan-under-row shape is the KOA-18016 corruption that made columns unselectable and
 * blocked further drops. `StaticTableColumn` is intentionally NOT listed: it is the cell and hosts
 * arbitrary content.
 *
 * Hardcoded to the real tree structure (not metadata `childrenNames`, which is incomplete for the
 * family: StaticTable lists only StaticTableRow, StaticTableHeader lists none) and matches the
 * markup translator's `<StaticTable><StaticTableHeader|StaticTableRow><StaticTableColumn>` output.
 */
export const STATICTABLE_STRUCTURAL_CHILDREN: Readonly<Record<string, ReadonlySet<string>>> = {
  StaticTable: new Set(["StaticTableRow", "StaticTableHeader"]),
  StaticTableHeader: new Set(["StaticTableColumn"]),
  StaticTableRow: new Set(["StaticTableColumn"])
};

/** Menu-like parents that only accept MenuItem/SubMenu. */
export const MENU_TARGETS: ReadonlySet<string> = new Set(["Menu", "MenuItem", "SubMenu"]);

/** Valid children of Menu-like parents. */
export const MENU_CHILDREN: ReadonlySet<string> = new Set(["MenuItem", "SubMenu"]);

/** Auto-generated column types that don't accept arbitrary children. */
export const AUTO_COLUMNS: ReadonlySet<string> = new Set(["EditableTableColumn", "TableDefaultColumn"]);
